import type { AppStore } from "../Store";

import VersionsAPIConnector from "@/Services/TherapyApi/Versions";

import { config } from "@/Common/envConfig";

import { isAndroidApp, isApp, isIosApp } from "@/Utils/Ionic";
import { hasValue } from "@/Utils/Objects";
import { danger, log } from "@/Utils/Debug";

class CheckUpdateActionHandler {
  constructor(
    private readonly appStore: AppStore,
    private readonly versionApiConnector: VersionsAPIConnector = new VersionsAPIConnector()
  ) {}

  public async handle(): Promise<void> {
    if (!isApp()) return;

    try {
      const versionRequirements = await this.versionApiConnector.getRequirements();

      let currentVersion = undefined;
      let minAppVersion = undefined; // min app version api response
      let limitVersion = undefined;

      if (isIosApp()) {
        this.appStore.versionRequirements = versionRequirements.ios;
        currentVersion = config.iosAppVersion!;
        minAppVersion = versionRequirements.ios.min;
        limitVersion = versionRequirements.ios.current;
      } else if (isAndroidApp()) {
        this.appStore.versionRequirements = versionRequirements.android;
        currentVersion = parseInt(config.androidAppVersion!, 10);

        if (`${versionRequirements.android.min}`.includes(".")) {
          currentVersion = config.androidAppVersion!;
        } else {
          currentVersion = config.androidAppTimestamp!;
        }

        minAppVersion = versionRequirements.android.min;
        limitVersion = versionRequirements.android.current;
      }

      log(`FU -> min: ${minAppVersion}, max: ${limitVersion}, current: ${currentVersion}`);

      if (hasValue(currentVersion) && hasValue(minAppVersion) && hasValue(limitVersion)) {
        if (this._isVersionGreater(minAppVersion, currentVersion)) {
          this.appStore.needUpdate = true;
          this.appStore.isForceUpdate = true;
        } else if (!this._isVersionGreater(currentVersion, limitVersion)) {
          this.appStore.needUpdate = true;
          this.appStore.isForceUpdate = false;
        } else {
          this.appStore.needUpdate = false;
          this.appStore.isForceUpdate = false;
        }
      } else {
        this.appStore.needUpdate = false;
        this.appStore.isForceUpdate = false;
      }
    } catch (e) {
      danger("FU -> ERROR", e);
      this.appStore.needUpdate = false;
    }
  }

  _isVersionGreater(version1: string, version2: string): boolean {
    const parts1 = version1.split(".").map(Number);
    const parts2 = version2.split(".").map(Number);

    const maxLength = Math.max(parts1.length, parts2.length);

    for (let i = 0; i < maxLength; i++) {
      const v1 = parts1[i] || 0;
      const v2 = parts2[i] || 0;

      if (v1 > v2) return true;
      if (v1 < v2) return false;
    }

    return false;
  }
}

export default CheckUpdateActionHandler;
